import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AnnouncementsPaths,
  ListAnnouncementTemplatesResponse,
  SendAnnouncementResponse,
  TestSendAnnouncementResponse,
  RecipientList,
  type SendAnnouncementBody,
  type TestSendAnnouncementBody,
} from "../../client-messages/announcements";
import { announcementsRequest, type AnnouncementsApiConfig } from "./api";

/**
 * Announcements data leaves memory soon after the page closes. Every query
 * and mutation key starts with "announcements", which the app's persisted
 * cache never writes to disk (`shouldPersistQuery`, `shouldPersistMutation`):
 * lists and uploads hold customers' addresses.
 */
const ANNOUNCEMENTS_GC_MS = 2 * 60_000;

/**
 * The SendGrid `[Announcement]` templates. Read fresh on every visit: they
 * are edited in SendGrid, so a cached list could offer a template whose
 * unsubscribe link has since been removed.
 */
export function useAnnouncementTemplates(config: AnnouncementsApiConfig) {
  return useQuery({
    queryKey: ["announcements", "templates"],
    queryFn: ({ signal }) =>
      announcementsRequest(
        config,
        { method: "GET", path: AnnouncementsPaths.templates, signal },
        ListAnnouncementTemplatesResponse,
      ),
    staleTime: 0,
    gcTime: ANNOUNCEMENTS_GC_MS,
    refetchOnMount: "always",
    retry: false,
  });
}

/** Uploads a CSV as a new list; the answer is its summary. */
export function useUploadList(config: AnnouncementsApiConfig) {
  return useMutation({
    mutationKey: ["announcements", "upload"],
    mutationFn: (csv: string) =>
      announcementsRequest(
        config,
        { method: "POST", path: AnnouncementsPaths.lists, file: { contentType: "text/csv", text: csv } },
        RecipientList,
      ),
  });
}

/** Sends the announcement to the signed-in support user only. */
export function useTestSendAnnouncement(config: AnnouncementsApiConfig) {
  return useMutation({
    mutationKey: ["announcements", "test-send"],
    mutationFn: (body: TestSendAnnouncementBody) =>
      announcementsRequest(
        config,
        { method: "POST", path: AnnouncementsPaths.testSend, body },
        TestSendAnnouncementResponse,
      ),
  });
}

/** Sends the announcement to everyone on one stored list. Never retried automatically. */
export function useSendAnnouncement(config: AnnouncementsApiConfig) {
  return useMutation({
    mutationKey: ["announcements", "send"],
    mutationFn: (body: SendAnnouncementBody) =>
      announcementsRequest(config, { method: "POST", path: AnnouncementsPaths.send, body }, SendAnnouncementResponse),
    retry: false,
  });
}
